import { add } from "date-fns";
import { datatype, lorem, random } from "faker/locale/en_US";

import {
  defaultCloseReasons,
  defaultDealCategories,
  defaultDealStages,
} from "../../../root/defaultConfiguration";
import type { Deal } from "../../../types";
import type { Db } from "./types";
import { randomDate } from "./utils";

export const generateDeals = (db: Db): Deal[] => {
  const deals = Array.from(Array(50).keys()).map((id) => {
    const company = random.arrayElement(db.companies);
    company.nb_deals = (company.nb_deals ?? 0) + 1;
    const contacts = random.arrayElements(
      db.contacts.filter((contact) => contact.company_id === company.id),
      datatype.number({ min: 1, max: 3 }),
    );
    const lowercaseName = lorem.words();
    const created_at = randomDate(new Date(company.created_at)).toISOString();

    const expected_closing_date = randomDate(
      new Date(created_at),
      add(new Date(created_at), { months: 6 }),
    )
      .toISOString()
      .split("T")[0];

    const stage = random.arrayElement(defaultDealStages).value;
    const updated_at = randomDate(new Date(created_at)).toISOString();

    // Plausible stage history: walk forward through the open pipeline order,
    // ending at the current stage (closed deals jump from a mid stage).
    const isClosed = stage === "won" || stage === "lost";
    const openOrder = defaultDealStages
      .map((s) => s.value)
      .filter((s) => s !== "won" && s !== "lost");
    const walk = isClosed
      ? [...openOrder.slice(0, 1 + (id % 3)), stage]
      : openOrder.slice(0, Math.max(openOrder.indexOf(stage), 0) + 1);
    const createdMs = new Date(created_at).getTime();
    const updatedMs = new Date(updated_at).getTime();
    const stage_history = walk.map((s, i) => ({
      stage: s,
      date: new Date(
        createdMs +
          ((updatedMs - createdMs) * i) / Math.max(walk.length - 1, 1),
      ).toISOString(),
    }));

    return {
      id,
      name: lowercaseName[0].toUpperCase() + lowercaseName.slice(1),
      company_id: company.id,
      contact_ids: contacts.map((contact) => contact.id),
      category: random.arrayElement(defaultDealCategories).value,
      stage,
      description: lorem.paragraphs(datatype.number({ min: 1, max: 4 })),
      amount: datatype.number(1000) * 100,
      created_at,
      updated_at,
      expected_closing_date,
      sales_id: company.sales_id!,
      index: 0,
      closed_at: isClosed ? updated_at : null,
      close_reason:
        stage === "lost" ? random.arrayElement(defaultCloseReasons).value : null,
      stage_history,
    };
  });
  // compute index based on stage
  defaultDealStages.forEach((stage) => {
    deals
      .filter((deal) => deal.stage === stage.value)
      .forEach((deal, index) => {
        deals[deal.id].index = index;
      });
  });
  return deals;
};
